/**
 * ===========================================================================
 *  BASE CAPTURE — the meter, the flip and its consequences
 * ===========================================================================
 *
 * Owned by the mission layer (src/game/mission.js). Every capturable site in
 * `world.bases.list` gets one record here holding the live head-count inside
 * its radius and a 0..1 capture meter.
 *
 * The rule, taken from the reference plate (E, "Morale"): you take a site by
 * standing in it with your men while the enemy garrison is dead or driven
 * off. Presence is measured, not scripted — `entities.countIn()` at 10 Hz —
 * and the hero himself is worth five men, so a Dynasty Warriors player who
 * clears a camp alone still takes it.
 *
 * Nothing here allocates after `build()`.
 */
import { TEAM } from '../core/entities.js';
import { clamp01 } from '../core/mathx.js';

/** Seconds of uncontested presence needed to take a site, by type. */
const DUR = { fort: 26, camp: 17, post: 13, bridge: 12, outpost: 12 };

const HERO = 5;          // the hero is worth five men on a capture point
const MIN_BODIES = 3;    // one man cannot take a fort on his own
const EDGE = 1.7;        // how much you must outnumber the defender to advance
const RECOUNT = 0.1;     // seconds between head-counts (10 Hz)
const RELOCK = 9;        // seconds a site is safe after changing hands

export function createCaptureBoard() {
  const recs = [];
  const byId = new Map();
  const origin = new Map();          // authored team, snapshotted once
  let countT = 0;
  let onFlip = null;
  let onContest = null;

  /** Build one record per base. Safe to call again after a reset. */
  function build(w) {
    recs.length = 0; byId.clear();
    const list = (w.bases && w.bases.list) || [];
    for (let i = 0; i < list.length; i++) {
      const b = list[i];
      if (!origin.has(b.id)) origin.set(b.id, b.team | 0);
      const r = {
        id: b.id, name: b.name, type: b.type,
        x: b.x, z: b.z, radius: b.radius,
        team: b.team | 0, team0: origin.get(b.id),
        /** 0..1 meter toward `capTeam`. */
        progress: 0, capTeam: -1, contested: false, hero: false,
        ally: 0, enemy: 0, cool: 0, dur: DUR[b.type] || 15,
        spawns: b.spawns || [], base: b, logT: 0,
      };
      recs.push(r); byId.set(r.id, r);
    }
    countT = 0;
    return recs;
  }

  function reset(w) {
    countT = 0;
    for (let i = 0; i < recs.length; i++) {
      const r = recs[i];
      r.progress = 0; r.capTeam = -1; r.contested = false; r.cool = 0;
      r.ally = r.enemy = 0; r.hero = false; r.logT = 0;
      if (r.base.team !== r.team0) w.bases?.setTeam?.(r.id, r.team0);
      r.team = r.team0;
    }
  }

  /** Live head-count inside every base. Cheap: 2 hash queries per site. */
  function recount(w) {
    const e = w.entities, pid = w.playerId;
    const px = pid >= 0 ? e.x[pid] : 1e9, pz = pid >= 0 ? e.z[pid] : 1e9;
    for (let i = 0; i < recs.length; i++) {
      const r = recs[i];
      r.ally = e.countIn(r.x, r.z, r.radius, TEAM.PLAYER);
      r.enemy = e.countIn(r.x, r.z, r.radius, TEAM.ENEMY);
      const dx = px - r.x, dz = pz - r.z;
      r.hero = dx * dx + dz * dz < r.radius * r.radius;
    }
  }

  /**
   * Integrate every meter. `dt` is the fixed step; the head-count behind it
   * is at most 0.1 s stale, which no player can see.
   */
  function update(w, dt) {
    countT -= dt;
    if (countT <= 0) { countT = RECOUNT; recount(w); }

    for (let i = 0; i < recs.length; i++) {
      const r = recs[i];
      r.team = r.base.team | 0;
      if (r.cool > 0) { r.cool -= dt; r.progress = 0; r.capTeam = -1; continue; }

      const A = r.ally + (r.hero ? HERO : 0);
      const E = r.enemy;
      r.contested = A >= 2 && E >= 2;

      let want = -1;
      if (A >= MIN_BODIES && A > E * EDGE && r.team !== TEAM.PLAYER) want = TEAM.PLAYER;
      else if (E >= MIN_BODIES && E > A * EDGE && r.team !== TEAM.ENEMY) want = TEAM.ENEMY;

      if (want < 0) {
        if (r.progress > 0) {
          r.progress -= dt * (r.contested ? 0.018 : 0.085);
          if (r.progress <= 0) { r.progress = 0; r.capTeam = -1; }
        }
        continue;
      }

      if (want !== r.capTeam) {
        r.capTeam = want;
        r.progress *= 0.3;
        if (onContest) onContest(w, r, want);
      }
      const force = want === TEAM.PLAYER ? A : E;
      r.progress += dt * (1 / r.dur) * (0.55 + 0.55 * clamp01(force / 10));
      if (r.progress >= 1) {
        r.progress = 0; r.capTeam = -1; r.cool = RELOCK;
        r.team = want;
        w.bases?.setTeam?.(r.id, want);        // banner colour + 'base:captured'
        if (onFlip) onFlip(w, r, want);
      }
    }
  }

  /** The meter worth showing: the one the hero is standing in, else the highest. */
  function leader() {
    let best = null;
    for (let i = 0; i < recs.length; i++) {
      const r = recs[i];
      if (r.progress <= 0.005) continue;
      if (!best) { best = r; continue; }
      const a = r.progress + (r.hero ? 1 : 0), b = best.progress + (best.hero ? 1 : 0);
      if (a > b) best = r;
    }
    return best;
  }

  function held(team) {
    let n = 0;
    for (let i = 0; i < recs.length; i++) if ((recs[i].base.team | 0) === team) n++;
    return n;
  }

  return {
    recs, byId, build, reset, update, leader, held,
    get(id) { return byId.get(id) || null; },
    progressOf(id) { const r = byId.get(id); return r ? r.progress : 0; },
    set onFlip(fn) { onFlip = fn; },
    set onContest(fn) { onContest = fn; },
  };
}
